import React, { useState, useEffect } from 'react';
import { Container, Tabs, Badge, Text, Paper, Group, SimpleGrid, Card, Stack } from '@mantine/core';
import { Helmet } from 'react-helmet-async';
import { useAppSelector } from '../../store/hooks';
import { useSEO } from '../../hooks/useSEO';
import ReactCountryFlag from "react-country-flag";
import { Loader } from '../../components/common/Loader';

// Country code mapping for react-country-flag
const countryCodeMap: Record<string, string> = {
  'United States': 'US',
  'USA': 'US',
  'United Arab Emirates': 'AE',
  'UAE': 'AE',
  'China': 'CN',
  'Singapore': 'SG',
  'United Kingdom': 'GB',
  'UK': 'GB',
  'Netherlands': 'NL',
  'Germany': 'DE',
  'Hong Kong': 'HK',
  'Bangladesh': 'BD',
  'Vietnam': 'VN',
  'Malaysia': 'MY',
  'Belgium': 'BE',
  'Sri Lanka': 'LK',
  'Indonesia': 'ID',
  'Saudi Arabia': 'SA',
  'France': 'FR',
  'Thailand': 'TH',
  'Japan': 'JP',
  'Italy': 'IT',
  'South Korea': 'KR',
  'Turkey': 'TR',
  'Australia': 'AU',
  'Canada': 'CA',
  'Brazil': 'BR',
  'South Africa': 'ZA',
  'Mexico': 'MX',
  'Egypt': 'EG',
  'Russia': 'RU',
  'Poland': 'PL',
  'Spain': 'ES',
  'Kenya': 'KE',
  'Nigeria': 'NG',
  'Tanzania': 'TZ',
  'Nepal': 'NP',
  'Myanmar': 'MM',
  'Oman': 'OM',
  'Qatar': 'QA',
  'Kuwait': 'KW',
  'Bahrain': 'BH',
  'Iran': 'IR',
  'Iraq': 'IQ',
  'Jordan': 'JO',
  'Lebanon': 'LB',
  'Switzerland': 'CH',
  'Sweden': 'SE',
  'Denmark': 'DK',
  'Norway': 'NO',
  'Finland': 'FI',
  'Ireland': 'IE',
  'Portugal': 'PT'
};

const getCountryCode = (countryName: string): string => {
  if (countryCodeMap[countryName]) return countryCodeMap[countryName];
  for (const [key, value] of Object.entries(countryCodeMap)) {
    if (countryName.includes(key) || key.includes(countryName)) return value;
  }
  return 'UN';
};

interface Country {
  id: string;
  name: string;
  continent: string;
  isActive: boolean;
}

export const InternationalPage: React.FC = () => {
  const { content } = useAppSelector((state) => state.content);
  const countries: Country[] = content?.pages?.international?.countries || [];
  const { seo, loading: seoLoading } = useSEO('international');
  const [loading, setLoading] = useState(true);
  const [selectedContinent, setSelectedContinent] = useState<string>('all');

  const continents = ['all', ...new Set(countries.map(c => c.continent))];
  const filteredCountries = selectedContinent === 'all' 
    ? countries 
    : countries.filter(c => c.continent === selectedContinent);

  useEffect(() => {
    setTimeout(() => setLoading(false), 500);
  }, []);

  const isLoading = loading || seoLoading;

  if (isLoading) {
    return (
      <Container size="xl" className="flex justify-center items-center py-12">
        <Loader />
      </Container>
    );
  }

  return (
    <>
      <Helmet>
        <title>{seo?.metaTitle}</title>
        <meta name="description" content={seo?.metaDescription} />
        <meta name="keywords" content={seo?.metaKeywords?.join(', ')} />
        <meta property="og:title" content={seo?.ogTitle} />
        <meta property="og:description" content={seo?.ogDescription} />
        {seo?.ogImage && <meta property="og:image" content={seo.ogImage} />}
        <meta name="twitter:title" content={seo?.twitterTitle} />
        <meta name="twitter:description" content={seo?.twitterDescription} />
        {seo?.twitterImage && <meta name="twitter:image" content={seo.twitterImage} />}
        {seo?.canonicalUrl && <link rel="canonical" href={seo.canonicalUrl} />}
      </Helmet>

      <Container size="xl">
        <Tabs defaultValue="flags">
          <Tabs.List>
            <Tabs.Tab value="flags">Countries We Export To</Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="flags" pt="xl">
            {/* Stats Section */}
            <Paper withBorder p="md" radius="md" mb="lg">
              <Group justify="space-between">
                <div>
                  <Text size="lg" fw={600}>Global Presence</Text>
                  <Text size="sm" c="dimmed">
                    We proudly export to <strong>{countries.length}</strong> countries across the globe
                  </Text>
                </div>
                <Badge color="primary" size="lg" variant="light">
                  Export Destinations
                </Badge>
              </Group>
            </Paper>

            {/* Continent Filter */}
            <Paper withBorder p="md" radius="md" mb="lg">
              <Text size="sm" fw={500} mb="xs">Filter by Continent:</Text>
              <Group gap="xs">
                {continents.map(continent => (
                  <Badge
                    key={continent}
                    color={selectedContinent === continent ? "primary" : "gray"}
                    variant={selectedContinent === continent ? "filled" : "light"}
                    size="lg"
                    style={{ cursor: 'pointer' }}
                    onClick={() => setSelectedContinent(continent)}
                  >
                    {continent === 'all' ? 'All Continents' : continent}
                  </Badge>
                ))}
              </Group>
            </Paper>

            {/* Countries Grid */}
            <Paper withBorder p="md" radius="md">
              <Text size="sm" fw={600} mb="md">
                Export Destinations ({filteredCountries.length} countries)
              </Text>

              <SimpleGrid cols={{ base: 2, sm: 3, md: 4, lg: 5 }} spacing="md">
                {filteredCountries.map((country) => {
                  const countryCode = getCountryCode(country.name);
                  return (
                    <Card 
                      key={country.id} 
                      withBorder 
                      padding="sm" 
                      radius="md" 
                      className="hover:shadow-md transition-shadow"
                    >
                      <Stack align="center" gap={8}>
                        <div className="w-16 h-12 flex items-center justify-center">
                          <ReactCountryFlag
                            countryCode={countryCode}
                            svg
                            style={{
                              width: '100%',
                              height: 'auto',
                              maxHeight: '48px',
                              objectFit: 'contain',
                              borderRadius: '4px',
                              boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                            }}
                            title={country.name}
                          />
                        </div>
                        <Text size="sm" fw={500} ta="center" lineClamp={1}>
                          {country.name}
                        </Text>
                        <Badge color="primary" size="xs" variant="light">
                          {country.continent}
                        </Badge>
                      </Stack>
                    </Card>
                  );
                })}
              </SimpleGrid>

              {filteredCountries.length === 0 && (
                <Text c="dimmed" ta="center" py={8}>
                  No countries found in this continent.
                </Text>
              )}
            </Paper>

            {/* Info Card */}
            <Paper withBorder p="md" mt="lg" radius="md" style={{ backgroundColor: 'var(--mantine-color-primary-light)' }}>
              <Text size="sm" fw={600} mb="xs">🌍 Our Global Reach</Text>
              <Text size="sm" c="dimmed">
                From Asia to Europe, North America to Africa, Thalvora Exports delivers premium 
                agricultural products to customers worldwide. Our extensive logistics network ensures 
                fresh and quality products reach your destination on time, every time.
              </Text>
              <Group justify="space-between" mt="md">
                <Text size="xs" c="dimmed">
                  Countries: {countries.length} | Continents: {continents.length - 1}
                </Text>
                <Text size="xs" c="dimmed">
                  Updated: {new Date().toLocaleDateString()}
                </Text>
              </Group>
            </Paper>
          </Tabs.Panel>
        </Tabs>
      </Container>
    </>
  );
};